import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useEffect } from "react";
import { POLLING_INTERVAL_MS } from "../../constants/polling-constants";
import { queryKeys } from "../../lib/queryKeys";
import { getOpcdCodingAlerts } from "../../services";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { setOpcdCodingAlertsCount } from "../../store/providerReducer";
import type { ProFeeAuditAlertsResponse } from "../../types/pro-fee-audit";
import {
  countProFeeAuditAlerts,
  normalizeProFeeAuditAlertsResponse,
} from "../../utils/pro-fee-audit";
import { useOpcdEncounterLookups } from "./useOpcdEncounterLookups";

const fetchOpcdCodingAlerts = async (params: {
  providerNpi: string;
  mrn: string;
  dateOfService?: string;
}): Promise<ProFeeAuditAlertsResponse> => {
  try {
    const response = await getOpcdCodingAlerts(params);
    return normalizeProFeeAuditAlertsResponse(response.data);
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return normalizeProFeeAuditAlertsResponse(error.response?.data);
    }
    throw error;
  }
};

export const useOpcdCodingAlerts = (options?: {
  polling?: boolean;
  enabled?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const storedCount = Number(
    useAppSelector((state) => state.provider.opcdCodingAlertsCount) || 0
  );
  const { providerNpi, mrn, dateOfService } = useOpcdEncounterLookups();
  const enabled =
    (options?.enabled ?? true) && Boolean(providerNpi && mrn);

  const query = useQuery({
    queryKey: queryKeys.opcdCodingAlerts(
      providerNpi,
      mrn,
      dateOfService ?? ""
    ),
    queryFn: () =>
      fetchOpcdCodingAlerts({
        providerNpi,
        mrn,
        dateOfService,
      }),
    enabled,
    refetchInterval: options?.polling ? POLLING_INTERVAL_MS : false,
    retry: 0,
  });

  useEffect(() => {
    if (!enabled) return;
    if (!query.data && !query.isError) return;
    const count = countProFeeAuditAlerts(query.data);
    if (count !== storedCount) {
      dispatch(setOpcdCodingAlertsCount(count));
    }
  }, [dispatch, enabled, query.data, query.isError, storedCount]);

  return query;
};

export const useOpcdCodingAlertsCount = (options?: {
  polling?: boolean;
  enabled?: boolean;
}) => {
  const { providerNpi, mrn, dateOfService } = useOpcdEncounterLookups();
  const enabled =
    (options?.enabled ?? true) && Boolean(providerNpi && mrn);

  return useQuery({
    queryKey: queryKeys.opcdCodingAlertsCount(
      providerNpi,
      mrn,
      dateOfService ?? ""
    ),
    queryFn: async () => {
      const response = await fetchOpcdCodingAlerts({
        providerNpi,
        mrn,
        dateOfService,
      });
      return countProFeeAuditAlerts(response);
    },
    enabled,
    refetchInterval: options?.polling ? POLLING_INTERVAL_MS : false,
    retry: 0,
  });
};
